import type { FC, PropsWithChildren, ReactElement } from 'react';
import {
    Modal as ChakraModal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    useBreakpointValue,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import type { ModalProps as ChakraModalProps, StyleProps } from '@chakra-ui/react';
import { GetIcon } from '../../components/icons';
import { isModernBrowser } from '../../utils/utils';
import { Drawer } from '../drawer';
import { Header } from '../typography';

export type ModalProps = PropsWithChildren<{
    isOpen: boolean;
    onClose: () => void;
    title?: string | ReactElement;
    id?: string;
    variant?: ChakraModalProps['variant'];
    modalOnly?: boolean;
    size?: ChakraModalProps['size'];
    containerStyle?: StyleProps;
}>;

export const Modal: FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    id = '',
    variant = 'ui-default',
    modalOnly = false,
    size = 'compact',
    containerStyle,
}) => {
    const renderer = useBreakpointValue({
        base: 'drawer',
        md: 'modal',
    });

    if (renderer === 'drawer' && !modalOnly) {
        return (
            <Drawer
                id={id}
                isOpen={isOpen}
                onClose={onClose}
                title={typeof title === 'string' ? title || '' : ''}
                containerStyle={containerStyle}
            >
                {children}
            </Drawer>
        );
    }

    return (
        <ChakraModal
            variant={variant}
            isOpen={isOpen}
            onClose={onClose}
            id={id}
            autoFocus={false}
            size={size}
        >
            <ModalOverlay />
            <ModalContent data-testid={id}>
                {title ? (
                    <ModalHeader
                        flex={isModernBrowser ? undefined : 'unset'}
                        display="flex"
                        alignItems="center"
                        justifyItems="center"
                    >
                        <div className="flex-1">
                            {typeof title === 'string' ? (
                                <Header
                                    id={`${id}-title`}
                                    as="h4"
                                    style="bold"
                                    color="slt-pepper.1000"
                                    variant="header4"
                                >
                                    {title}
                                </Header>
                            ) : (
                                title
                            )}
                        </div>
                        <ModalCloseButton
                            data-testid={`${id ?? 'modal'}-close-button`}
                            position="static"
                        >
                            <GetIcon name="close" size="md" />
                        </ModalCloseButton>
                    </ModalHeader>
                ) : (
                    <ModalCloseButton data-testid={`${id ?? 'modal'}-close-button`}>
                        <GetIcon name="close" size="md" />
                    </ModalCloseButton>
                )}
                <ModalBody {...containerStyle}>{children}</ModalBody>
            </ModalContent>
        </ChakraModal>
    );
};

Modal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    id: PropTypes.string,
    variant: PropTypes.string,
};

Modal.defaultProps = {
    id: undefined,
    variant: 'ui-default',
    modalOnly: false,
    title: undefined,
    size: undefined,
    containerStyle: undefined,
};
